/**
 * SC-08: 取引先・発注先マスタ
 *
 * 取引先・発注先の一覧・登録・編集画面
 * タブで取引先と発注先を切り替え
 *
 * @created 2025-11-16
 * @updated 2025-11-26 - 発注先マスタ追加
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import { Add, Edit, Delete, FilterList } from '@mui/icons-material';
import { salesMasterApi } from '../../services/salesApi';
import type { Account, Vendor } from '../../services/salesApi';
import { extractErrorMessage } from '../../utils/errorHandler';
import RequiredFieldLabel from '../../components/Common/RequiredFieldLabel';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const AccountMasterPage: React.FC = () => {
  // タブ
  const [tabValue, setTabValue] = useState(0);

  // 共通
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // フォームのフィールド別エラー（フォームUX_バリデーション共通設計方針 §4）
  const [accountNameError, setAccountNameError] = useState('');
  const [vendorNameError, setVendorNameError] = useState('');

  // 取引先
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [accountDisplayLimit, setAccountDisplayLimit] = useState(0); // 0 = 全件表示
  const [accountFilterAnchorEl, setAccountFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountEditMode, setAccountEditMode] = useState(false);
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deleteAccountTarget, setDeleteAccountTarget] = useState<Account | null>(null);

  // 取引先フォーム
  const [accountName, setAccountName] = useState('');
  const [accountNameKana, setAccountNameKana] = useState('');
  const [accountIndustry, setAccountIndustry] = useState('');
  const [accountNotes, setAccountNotes] = useState('');

  // 発注先
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [vendorDisplayLimit, setVendorDisplayLimit] = useState(0); // 0 = 全件表示
  const [vendorFilterAnchorEl, setVendorFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [vendorEditMode, setVendorEditMode] = useState(false);
  const [currentVendor, setCurrentVendor] = useState<Vendor | null>(null);
  const [deleteVendorDialogOpen, setDeleteVendorDialogOpen] = useState(false);
  const [deleteVendorTarget, setDeleteVendorTarget] = useState<Vendor | null>(null);

  // 発注先フォーム
  const [vendorName, setVendorName] = useState('');
  const [vendorNameKana, setVendorNameKana] = useState('');
  const [vendorIndustry, setVendorIndustry] = useState('');
  const [vendorNotes, setVendorNotes] = useState('');

  useEffect(() => {
    if (tabValue === 0) {
      fetchAccounts();
    } else {
      fetchVendors();
    }
  }, [tabValue]);

  // ===============================
  // 取引先関連の関数
  // ===============================

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await salesMasterApi.getAccounts({ is_active: true });
      if (response.data.success) {
        setAccounts(response.data.data);
      }
    } catch (err) {
      console.error('取引先取得エラー:', err);
      const errorMessage = extractErrorMessage(err, '取引先の取得に失敗しました');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openAccountCreateDialog = () => {
    setAccountEditMode(false);
    setCurrentAccount(null);
    resetAccountForm();
    setAccountDialogOpen(true);
  };

  const openAccountEditDialog = (account: Account) => {
    setAccountEditMode(true);
    setCurrentAccount(account);
    setAccountName(account.account_name);
    setAccountNameKana(account.account_name_kana || '');
    setAccountIndustry(account.industry || '');
    setAccountNotes(account.notes || '');
    setAccountDialogOpen(true);
  };

  const resetAccountForm = () => {
    setAccountName('');
    setAccountNameKana('');
    setAccountIndustry('');
    setAccountNotes('');
  };

  const handleAccountSubmit = async () => {
    if (!accountName) {
      setAccountNameError('取引先名を入力してください');
      setError('入力内容を確認してください');
      return;
    }
    setAccountNameError('');

    try {
      const data = {
        account_name: accountName,
        account_name_kana: accountNameKana || undefined,
        industry: accountIndustry || undefined,
        notes: accountNotes || undefined,
      };

      if (accountEditMode && currentAccount) {
        await salesMasterApi.updateAccount(currentAccount.account_id, data);
        setSuccess('取引先情報を更新しました');
      } else {
        await salesMasterApi.createAccount(data);
        setSuccess('取引先を登録しました');
      }

      setAccountDialogOpen(false);
      fetchAccounts();
    } catch (err) {
      console.error('取引先保存エラー:', err);
      const errorMessage = extractErrorMessage(err, '取引先の保存に失敗しました');
      setError(errorMessage);
    }
  };

  const handleAccountDelete = async () => {
    if (!deleteAccountTarget) return;
    try {
      await salesMasterApi.deleteAccount(deleteAccountTarget.account_id);
      setSuccess('取引先を削除しました');
      setDeleteAccountDialogOpen(false);
      setDeleteAccountTarget(null);
      fetchAccounts();
    } catch (err) {
      console.error('取引先削除エラー:', err);
      const errorMessage = extractErrorMessage(err, '取引先の削除に失敗しました');
      setError(errorMessage);
    }
  };

  const filteredAccounts = accounts
    .filter((account) => account.account_name.toLowerCase().includes(accountSearchTerm.toLowerCase()))
    .slice(0, accountDisplayLimit || undefined);

  // ===============================
  // 発注先関連の関数
  // ===============================

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await salesMasterApi.getVendors({ is_active: true });
      if (response.data.success) {
        setVendors(response.data.data);
      }
    } catch (err) {
      console.error('発注先取得エラー:', err);
      const errorMessage = extractErrorMessage(err, '発注先の取得に失敗しました');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openVendorCreateDialog = () => {
    setVendorEditMode(false);
    setCurrentVendor(null);
    resetVendorForm();
    setVendorDialogOpen(true);
  };

  const openVendorEditDialog = (vendor: Vendor) => {
    setVendorEditMode(true);
    setCurrentVendor(vendor);
    setVendorName(vendor.vendor_name);
    setVendorNameKana(vendor.vendor_name_kana || '');
    setVendorIndustry(vendor.industry || '');
    setVendorNotes(vendor.notes || '');
    setVendorDialogOpen(true);
  };

  const resetVendorForm = () => {
    setVendorName('');
    setVendorNameKana('');
    setVendorIndustry('');
    setVendorNotes('');
  };

  const handleVendorSubmit = async () => {
    if (!vendorName) {
      setVendorNameError('発注先名を入力してください');
      setError('入力内容を確認してください');
      return;
    }
    setVendorNameError('');

    try {
      const data = {
        vendor_name: vendorName,
        vendor_name_kana: vendorNameKana || undefined,
        industry: vendorIndustry || undefined,
        notes: vendorNotes || undefined,
      };

      if (vendorEditMode && currentVendor) {
        await salesMasterApi.updateVendor(currentVendor.vendor_id, data);
        setSuccess('発注先情報を更新しました');
      } else {
        await salesMasterApi.createVendor(data);
        setSuccess('発注先を登録しました');
      }

      setVendorDialogOpen(false);
      fetchVendors();
    } catch (err) {
      console.error('発注先保存エラー:', err);
      const errorMessage = extractErrorMessage(err, '発注先の保存に失敗しました');
      setError(errorMessage);
    }
  };

  const handleVendorDelete = async () => {
    if (!deleteVendorTarget) return;
    try {
      await salesMasterApi.deleteVendor(deleteVendorTarget.vendor_id);
      setSuccess('発注先を削除しました');
      setDeleteVendorDialogOpen(false);
      setDeleteVendorTarget(null);
      fetchVendors();
    } catch (err) {
      console.error('発注先削除エラー:', err);
      const errorMessage = extractErrorMessage(err, '発注先の削除に失敗しました');
      setError(errorMessage);
    }
  };

  const filteredVendors = vendors
    .filter((vendor) => vendor.vendor_name.toLowerCase().includes(vendorSearchTerm.toLowerCase()))
    .slice(0, vendorDisplayLimit || undefined);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          取引先・発注先マスタ
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* タブ */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="取引先" />
          <Tab label="発注先" />
        </Tabs>
      </Box>

      {/* 取引先タブ */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {accountDisplayLimit > 0 && filteredAccounts.length < accounts.filter(a => a.account_name.toLowerCase().includes(accountSearchTerm.toLowerCase())).length
              ? `${filteredAccounts.length}件表示 / 全${accounts.filter(a => a.account_name.toLowerCase().includes(accountSearchTerm.toLowerCase())).length}件`
              : `全${filteredAccounts.length}件`
            }
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" startIcon={<Add />} onClick={openAccountCreateDialog}>
              新規登録
            </Button>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={(e) => setAccountFilterAnchorEl(e.currentTarget)}
            >
              フィルタ
            </Button>
          </Box>
        </Box>

        {/* 取引先フィルタポップオーバー */}
        <Popover
          open={Boolean(accountFilterAnchorEl)}
          anchorEl={accountFilterAnchorEl}
          onClose={() => setAccountFilterAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, width: 300, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="取引先名で検索"
              value={accountSearchTerm}
              onChange={(e) => setAccountSearchTerm(e.target.value)}
              size="small"
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>表示件数</InputLabel>
              <Select
                value={accountDisplayLimit}
                onChange={(e) => setAccountDisplayLimit(Number(e.target.value))}
                label="表示件数"
              >
                <MenuItem value={20}>20件</MenuItem>
                <MenuItem value={50}>50件</MenuItem>
                <MenuItem value={100}>100件</MenuItem>
                <MenuItem value={200}>200件</MenuItem>
                <MenuItem value={0}>全件</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  setAccountSearchTerm('');
                  setAccountDisplayLimit(0);
                }}
              >
                リセット
              </Button>
              <Button variant="contained" size="small" onClick={() => setAccountFilterAnchorEl(null)}>
                適用
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* 取引先一覧 */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>取引先名</TableCell>
                  <TableCell>カナ</TableCell>
                  <TableCell>業種</TableCell>
                  <TableCell>備考</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAccounts.length > 0 ? (
                  filteredAccounts.map((account) => (
                    <TableRow key={account.account_id} hover>
                      <TableCell>{account.account_name}</TableCell>
                      <TableCell>{account.account_name_kana || '-'}</TableCell>
                      <TableCell>{account.industry || '-'}</TableCell>
                      <TableCell>{account.notes || '-'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="編集">
                          <IconButton size="small" onClick={() => openAccountEditDialog(account)} color="primary">
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="削除">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeleteAccountTarget(account);
                              setDeleteAccountDialogOpen(true);
                            }}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        取引先が見つかりませんでした
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* 発注先タブ */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          <Button variant="contained" startIcon={<Add />} onClick={openVendorCreateDialog}>
            新規登録
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={(e) => setVendorFilterAnchorEl(e.currentTarget)}
          >
            フィルタ
          </Button>
        </Box>

        {/* 発注先フィルタポップオーバー */}
        <Popover
          open={Boolean(vendorFilterAnchorEl)}
          anchorEl={vendorFilterAnchorEl}
          onClose={() => setVendorFilterAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, width: 300, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="発注先名で検索"
              value={vendorSearchTerm}
              onChange={(e) => setVendorSearchTerm(e.target.value)}
              size="small"
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>表示件数</InputLabel>
              <Select
                value={vendorDisplayLimit}
                onChange={(e) => setVendorDisplayLimit(Number(e.target.value))}
                label="表示件数"
              >
                <MenuItem value={20}>20件</MenuItem>
                <MenuItem value={50}>50件</MenuItem>
                <MenuItem value={100}>100件</MenuItem>
                <MenuItem value={200}>200件</MenuItem>
                <MenuItem value={0}>全件</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  setVendorSearchTerm('');
                  setVendorDisplayLimit(0);
                }}
              >
                リセット
              </Button>
              <Button variant="contained" size="small" onClick={() => setVendorFilterAnchorEl(null)}>
                適用
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* 発注先一覧 */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>発注先名</TableCell>
                  <TableCell>カナ</TableCell>
                  <TableCell>業種</TableCell>
                  <TableCell>備考</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVendors.length > 0 ? (
                  filteredVendors.map((vendor) => (
                    <TableRow key={vendor.vendor_id} hover>
                      <TableCell>{vendor.vendor_name}</TableCell>
                      <TableCell>{vendor.vendor_name_kana || '-'}</TableCell>
                      <TableCell>{vendor.industry || '-'}</TableCell>
                      <TableCell>{vendor.notes || '-'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="編集">
                          <IconButton size="small" onClick={() => openVendorEditDialog(vendor)} color="primary">
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="削除">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeleteVendorTarget(vendor);
                              setDeleteVendorDialogOpen(true);
                            }}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        発注先が見つかりませんでした
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* 取引先登録・編集ダイアログ */}
      <Dialog open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{accountEditMode ? '取引先編集' : '取引先新規登録'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box>
              <RequiredFieldLabel label="取引先名" />
              <TextField
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                error={!!accountNameError}
                helperText={accountNameError}
                fullWidth
              />
            </Box>
            <TextField
              label="取引先名（カナ）"
              value={accountNameKana}
              onChange={(e) => setAccountNameKana(e.target.value)}
              fullWidth
            />
            <TextField label="業種" value={accountIndustry} onChange={(e) => setAccountIndustry(e.target.value)} fullWidth />
            <TextField
              label="備考"
              value={accountNotes}
              onChange={(e) => setAccountNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleAccountSubmit} variant="contained">
            {accountEditMode ? '更新' : '登録'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 取引先削除確認ダイアログ */}
      <Dialog open={deleteAccountDialogOpen} onClose={() => setDeleteAccountDialogOpen(false)}>
        <DialogTitle>取引先削除の確認</DialogTitle>
        <DialogContent>
          <Typography>
            「{deleteAccountTarget?.account_name}」を削除してもよろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAccountDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleAccountDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 発注先登録・編集ダイアログ */}
      <Dialog open={vendorDialogOpen} onClose={() => setVendorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{vendorEditMode ? '発注先編集' : '発注先新規登録'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box>
              <RequiredFieldLabel label="発注先名" />
              <TextField
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                error={!!vendorNameError}
                helperText={vendorNameError}
                fullWidth
              />
            </Box>
            <TextField
              label="発注先名（カナ）"
              value={vendorNameKana}
              onChange={(e) => setVendorNameKana(e.target.value)}
              fullWidth
            />
            <TextField label="業種" value={vendorIndustry} onChange={(e) => setVendorIndustry(e.target.value)} fullWidth />
            <TextField
              label="備考"
              value={vendorNotes}
              onChange={(e) => setVendorNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVendorDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleVendorSubmit} variant="contained">
            {vendorEditMode ? '更新' : '登録'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 発注先削除確認ダイアログ */}
      <Dialog open={deleteVendorDialogOpen} onClose={() => setDeleteVendorDialogOpen(false)}>
        <DialogTitle>発注先削除の確認</DialogTitle>
        <DialogContent>
          <Typography>
            「{deleteVendorTarget?.vendor_name}」を削除してもよろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteVendorDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleVendorDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountMasterPage;
